'use client';

import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LockIcon from '@mui/icons-material/Lock';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import { Box, InputAdornment, TextField, Typography, useTheme } from '@mui/material';

import { ActionButton } from '@/components';
import { useT } from '@/hooks';
import { disabledInputSx, formCaptionSx, formLabelSx, getEditableInputSx } from '@/styles';

export interface ProfileFormSectionProps {
  name: string;
  email?: string;
  phone?: string;
  isSaving: boolean;
  hasChanges: boolean;
  onNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSave: () => void;
}

export function ProfileFormSection({
  name,
  email,
  phone,
  isSaving,
  hasChanges,
  onNameChange,
  onSave,
}: ProfileFormSectionProps) {
  const { t } = useT();
  const theme = useTheme();
  const editableInputSx = getEditableInputSx(theme);

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" sx={formLabelSx}>
        {t('profile.name')}
      </Typography>
      <TextField
        fullWidth
        value={name}
        onChange={onNameChange}
        placeholder={t('profile.enterName')}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <PersonOutlineIcon sx={{ color: 'text.secondary', fontSize: 1.25 }} />
            </InputAdornment>
          ),
        }}
        sx={editableInputSx}
      />

      {!!(email || phone) && (
        <>
          <Typography variant="body2" sx={formLabelSx}>
            {email ? t('profile.email') : t('profile.phone')}
          </Typography>
          <TextField
            fullWidth
            value={email || phone || ''}
            disabled
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {email ? (
                    <EmailOutlinedIcon sx={{ color: 'text.secondary', fontSize: 1.25 }} />
                  ) : (
                    <PhoneOutlinedIcon sx={{ color: 'text.secondary', fontSize: 1.25 }} />
                  )}
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <LockIcon sx={{ color: 'text.disabled', fontSize: 1 }} />
                </InputAdornment>
              ),
            }}
            sx={disabledInputSx}
          />
          <Typography variant="caption" sx={formCaptionSx}>
            {email ? t('profile.emailCannotBeChanged') : t('profile.phoneCannotBeChanged')}
          </Typography>
        </>
      )}

      <Box sx={{ mt: 'auto' }}>
        <ActionButton
          label={t('profile.saveChanges')}
          loadingLabel={t('profile.saving')}
          isLoading={isSaving}
          disabled={!hasChanges}
          onClick={onSave}
        />
      </Box>
    </Box>
  );
}
